import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { MapPin, Calendar, CheckCircle2, QrCode } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { useSession } from '../lib/session';
import { getMyBadge } from '../lib/pass';

const SG_TZ = 'Asia/Singapore';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      timeZone: SG_TZ,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Member's badge for each claimed event pass / e-certificate — the same
// Swapcard-style "already logged in -> badge with a scannable QR" screen a
// door scanner reads badge_token off of. Renders nothing if the member isn't
// signed in, still loading, or has no passes yet — this is a bonus panel,
// not a page with its own empty state.
export default function MyPasses() {
  const session = useSession();
  const [passes, setPasses] = useState(undefined); // undefined = loading / not signed in

  useEffect(() => {
    if (!session) { setPasses(undefined); return undefined; }
    let live = true;
    getMyBadge().then((rows) => { if (live) setPasses(rows); }).catch(() => { if (live) setPasses([]); });
    return () => { live = false; };
  }, [session]);

  if (!session) return null;
  if (passes === undefined || passes.length === 0) return null;

  return (
    <div className="px-4 pt-4 space-y-3" data-testid="my-passes">
      <p className="text-[12px] uppercase tracking-wider font-semibold text-muted-foreground px-0.5">
        My passes &amp; e-certificates
      </p>
      {passes.map((p, i) => (
        <Card key={`${p.program}-${p.claimed_at || i}`} className="overflow-hidden" data-testid={`pass-card-${i}`}>
          <div className="h-1.5" style={{ backgroundColor: '#0F9488' }} />
          <CardContent className="p-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex items-start gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                  <QrCode className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate">{p.program}</p>
                  {(p.first_name || p.table_no) && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {p.first_name}
                      {p.table_no ? ` · Table ${p.table_no}` : ''}
                    </p>
                  )}
                </div>
              </div>
              {p.checked_in && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-1 whitespace-nowrap flex-shrink-0">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Checked in
                </span>
              )}
            </div>

            {(p.venue || p.starts_at) && (
              <div className="space-y-1 text-xs text-muted-foreground">
                {p.venue && (
                  <p className="flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    {p.venue}{p.address ? ` · ${p.address}` : ''}
                  </p>
                )}
                {p.starts_at && (
                  <p className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                    {formatWhen(p.starts_at)} SGT
                  </p>
                )}
              </div>
            )}

            {p.ecert_body && (
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{p.ecert_body}</p>
            )}

            {/* Badge QR — a real scannable code encoding badge_token, the exact
                string the door scanner reads. Falls back to a plain badge (no
                code) if the backend didn't issue one for this row. */}
            <div className="flex flex-col items-center pt-1">
              {p.badge_token ? (
                <div
                  className="rounded-2xl bg-white p-3.5 shadow-md border border-border/60"
                  data-testid={`pass-qr-${i}`}
                >
                  <QRCodeCanvas
                    value={p.badge_token}
                    size={180}
                    bgColor="#ffffff"
                    fgColor="#0F172A"
                    level="M"
                    marginSize={2}
                  />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-lg border-2 border-dashed border-border bg-muted/40 flex items-center justify-center">
                  <QrCode className="h-9 w-9 text-muted-foreground" strokeWidth={1.5} />
                </div>
              )}
              {p.status && (
                <p className="mt-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {p.status}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
